import { usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { useCallback, useEffect, useRef, useState } from 'react'

import { toast } from 'sonner'

import { canUseApplePay, loadApplePaySdk } from '@/lib/apple-pay'
import paymentService from '@/services/payment-service'

interface ApplePayButtonProps {
	/** Amount in EUR. */
	amount: number
	/** Shown on the Apple Pay sheet. */
	label: string
	/** Merchant name shown during Apple merchant validation. */
	displayName?: string
	/** Checked once the sheet opens; return false to abort (e.g. stand no longer free). */
	onBeforePay?: () => Promise<boolean> | boolean
	/** Receives the PayPal capture id, same value the PayPal button produces. */
	onApproved: (captureId: string) => Promise<void> | void
	/** Lets the parent reveal both payment buttons at the same moment. */
	onStatusChange?: (status: ApplePayStatus) => void
}

export type ApplePayStatus = 'pending' | 'ready' | 'unavailable'

/**
 * Apple Pay through PayPal.
 * PayPal does not expose Apple Pay as a Smart Button funding source, so this uses the
 * `applepay` SDK component plus Apple's own button element and ApplePaySession.
 * The resulting capture id is handed back so the existing booking flow stays unchanged.
 */
export const ApplePayButton = ({
	amount,
	label,
	displayName = 'minmini',
	onBeforePay,
	onApproved,
	onStatusChange,
}: ApplePayButtonProps) => {
	const [{ isResolved }] = usePayPalScriptReducer()
	const [config, setConfig] = useState<any>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const isPayingRef = useRef(false)

	// Read through a ref so the native click listener never sees stale props.
	const latest = useRef({ amount, label, displayName, onBeforePay, onApproved })
	useEffect(() => {
		latest.current = { amount, label, displayName, onBeforePay, onApproved }
	}, [amount, label, displayName, onBeforePay, onApproved])

	const statusRef = useRef(onStatusChange)
	useEffect(() => {
		statusRef.current = onStatusChange
	}, [onStatusChange])

	const report = useCallback((status: ApplePayStatus) => {
		statusRef.current?.(status)
	}, [])

	// Apple's SDK does not depend on the PayPal one, so fetch it right away instead of
	// leaving it on the critical path behind the PayPal script.
	useEffect(() => {
		if (!canUseApplePay()) {
			report('unavailable')
			return
		}

		loadApplePaySdk().catch(error => {
			console.error('Apple Pay SDK failed to load:', error)
			report('unavailable')
		})
	}, [report])

	useEffect(() => {
		if (!isResolved || !canUseApplePay()) return

		let cancelled = false

		const setup = async () => {
			try {
				const applepay = (window as any).paypal?.Applepay?.()
				if (!applepay) return report('unavailable')

				const [applepayConfig] = await Promise.all([applepay.config(), loadApplePaySdk()])

				if (cancelled) return

				if (!applepayConfig?.isEligible) {
					console.warn('Apple Pay is not eligible for this domain/account.', applepayConfig)
					return report('unavailable')
				}

				setConfig(applepayConfig)
				report('ready')
			} catch (error) {
				console.error('Apple Pay is unavailable:', error)
				if (!cancelled) report('unavailable')
			}
		}

		setup()

		return () => {
			cancelled = true
		}
	}, [isResolved, report])

	/**
	 * Safari only allows `new ApplePaySession()` straight out of a user gesture, so this
	 * must stay synchronous. Anything async (the availability re-check, order creation)
	 * happens inside the session callbacks.
	 */
	const startApplePay = useCallback(() => {
		if (!config || isPayingRef.current) return

		const ApplePaySession = (window as any).ApplePaySession
		const applepay = (window as any).paypal?.Applepay?.()

		if (!ApplePaySession || !applepay) return

		isPayingRef.current = true

		let session: any

		try {
			session = new ApplePaySession(4, {
				countryCode: config.countryCode,
				merchantCapabilities: config.merchantCapabilities,
				supportedNetworks: config.supportedNetworks,
				currencyCode: 'EUR',
				requiredBillingContactFields: ['postalAddress'],
				total: {
					label: latest.current.label,
					type: 'final',
					amount: latest.current.amount.toFixed(2),
				},
			})
		} catch (error) {
			isPayingRef.current = false
			console.error('Apple Pay session could not be created:', error)
			toast.error('Apple Pay konnte nicht gestartet werden. Bitte nutzen Sie PayPal.')
			return
		}

		session.onvalidatemerchant = async (event: any) => {
			try {
				const { onBeforePay: check } = latest.current

				if (check) {
					const canProceed = await check()
					if (!canProceed) {
						session.abort()
						isPayingRef.current = false
						return
					}
				}

				const { merchantSession } = await applepay.validateMerchant({
					validationUrl: event.validationURL,
					displayName: latest.current.displayName,
				})

				session.completeMerchantValidation(merchantSession)
			} catch (error) {
				isPayingRef.current = false
				console.error('Apple Pay merchant validation failed:', error)
				session.abort()
				toast.error('Apple Pay konnte nicht bestätigt werden. Bitte nutzen Sie PayPal.')
			}
		}

		session.onpaymentauthorized = async (event: any) => {
			try {
				const orderId = await paymentService.createPayPalOrder({
					amount: latest.current.amount,
					description: latest.current.label,
				})

				await applepay.confirmOrder({
					orderId,
					token: event.payment.token,
					billingContact: event.payment.billingContact,
				})

				const captureId = await paymentService.capturePayPalOrder(orderId)

				session.completePayment(ApplePaySession.STATUS_SUCCESS)
				await latest.current.onApproved(captureId)
			} catch (error: any) {
				console.error('Apple Pay payment failed:', error)
				session.completePayment(ApplePaySession.STATUS_FAILURE)
				toast.error(
					error?.response?.data?.message ??
						error?.message ??
						'Die Zahlung mit Apple Pay ist fehlgeschlagen.',
				)
			} finally {
				isPayingRef.current = false
			}
		}

		session.oncancel = () => {
			isPayingRef.current = false
		}

		session.begin()
	}, [config])

	useEffect(() => {
		const container = containerRef.current
		if (!config || !container) return

		container.innerHTML =
			'<apple-pay-button buttonstyle="black" type="buy" locale="de"></apple-pay-button>'

		// Apple's element renders its button inside a shadow root, so listen on the element
		// itself rather than relying on a React handler further up the tree.
		const button = container.firstElementChild
		button?.addEventListener('click', startApplePay)

		return () => {
			button?.removeEventListener('click', startApplePay)
		}
	}, [config, startApplePay])

	if (!config) return null

	return (
		<div
			ref={containerRef}
			className="w-full [&>apple-pay-button]:w-full [&>apple-pay-button]:[--apple-pay-button-height:48px] [&>apple-pay-button]:[--apple-pay-button-border-radius:12px] lg:[&>apple-pay-button]:[--apple-pay-button-height:56px]"
		/>
	)
}
